import { Component } from '@theme/component';
import { sessionStart, remainingSeats, bookingPrice, participantNames, validPhone } from './hoppipolla-workshop-utils.js';

/** @extends {Component<{form: HTMLFormElement, session: HTMLSelectElement, quantity: HTMLSelectElement, names: HTMLTextAreaElement, phone: HTMLInputElement, total: HTMLElement, availability: HTMLElement, submit: HTMLButtonElement, status: HTMLElement}>} */
class HoppipollaWorkshop extends Component {
  requiredRefs = ['form', 'session', 'quantity', 'names', 'phone', 'total', 'availability', 'submit', 'status'];
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#prepare();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#prepare();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#timer);
  }

  #prepare() {
    clearInterval(this.#timer);
    this.refresh();
    this.#timer = setInterval(() => this.refresh(), 30000);
  }

  /** Update availability and price without replacing any customer-entered fields. */
  refresh() {
    const { session, quantity, total, availability, submit } = this.refs;
    for (const option of session.options) {
      if (!option.value) continue;
      const expired = !(sessionStart(option.dataset.date, option.dataset.time) > Date.now());
      const remaining = remainingSeats(option.dataset.stock, option.dataset.cart);
      option.disabled = expired || remaining === 0;
    }
    const selected = session.selectedOptions[0];
    const seats = selected?.value && !selected.disabled ? remainingSeats(selected.dataset.stock, selected.dataset.cart) : 0;
    for (const option of quantity.options) option.disabled = Number(option.value) > seats;
    if (Number(quantity.value) > seats) quantity.value = seats > 0 ? String(seats) : '1';
    submit.disabled = seats < 1;
    availability.textContent = selected?.value ? (seats ? this.dataset.seats.replace('[count]', seats) : this.dataset.unavailable) : this.dataset.choose;
    if (seats) {
      const price = bookingPrice(Number(quantity.value), Number(selected.dataset.price), Number(selected.dataset.cart));
      const money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
      total.textContent = `${money.format(price.total / 100)} AUD · ${quantity.value} × ${money.format(price.unit / 100)}`;
    } else total.textContent = this.dataset.priceHint;
    this.refs.names.setCustomValidity('');
    this.refs.phone.setCustomValidity('');
    this.refs.status.textContent = '';
  }

  /** Clear custom errors so native form validation can run again after edits. */
  clearValidation() {
    this.refs.names.setCustomValidity('');
    this.refs.phone.setCustomValidity('');
    this.refs.status.textContent = '';
  }

  /** @param {SubmitEvent} event - Validate details before a native booking form submission. */
  handleSubmit(event) {
    const { form, names, phone, quantity, session, status } = this.refs;
    // Draft mode never reaches Shopify's cart endpoint, even after valid input.
    if (this.dataset.enabled !== 'true') event.preventDefault();
    const selected = session.selectedOptions[0];
    const available = selected?.value && !selected.disabled && sessionStart(selected.dataset.date, selected.dataset.time) > Date.now();
    const seats = selected ? remainingSeats(selected.dataset.stock, selected.dataset.cart) : 0;
    if (!available || Number(quantity.value) > seats) {
      event.preventDefault();
      this.refresh();
      status.textContent = this.dataset.unavailable;
      session.focus();
      return;
    }
    names.setCustomValidity(participantNames(names.value).length === Number(quantity.value) ? '' : this.dataset.namesError);
    phone.setCustomValidity(validPhone(phone.value) ? '' : this.dataset.phoneError);
    if (!form.reportValidity()) {
      event.preventDefault();
      return;
    }
    if (this.dataset.enabled !== 'true') {
      status.textContent = this.dataset.review.replace('[session]', selected.textContent.trim()).replace('[total]', this.refs.total.textContent);
      status.focus();
    }
    // Live mode retains the native product form; Shopify rechecks inventory and discounts.
  }
}

if (!customElements.get('hoppipolla-workshop')) customElements.define('hoppipolla-workshop', HoppipollaWorkshop);
